import { type DOMAttributes, type ReactElement, type ReactNode } from "react"

import {
  OverlayArrow,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
  type TooltipTriggerComponentProps,
  type TooltipProps as AriaTooltipProps,
  Focusable,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"

export type Placement = AriaTooltipProps["placement"]

export type TooltipProps = {
  /** The trigger element for the tooltip. For now, should be a react-aria
   * Button, but in the future we could consider using useTrigger and
   * useTooltipTrigger in the future. */
  children: ReactNode
  content: ReactNode
  delay?: TooltipTriggerComponentProps["delay"]
  showArrow?: boolean
  /** Props to pass to the tooltip popover */
  tooltipProps?: Omit<AriaTooltipProps, "className" | "placement"> & {
    className?: string
    placement?: Placement
  }
  /** Props to pass to the tooltip trigger */
  triggerProps?: Partial<TooltipTriggerComponentProps>
}

// FocusableElement is copied from @react-types/shared
interface FocusableElement extends Element, HTMLOrSVGElement {}
export type CustomTriggerChildren = ReactElement<
  DOMAttributes<FocusableElement>,
  string
>

/**
 * Allows for using arbitrary elements as the trigger for a tooltip. See
 * example:
 * https://react-spectrum.adobe.com/react-aria/Tooltip.html#custom-trigger
 *
 * Wrapped elements must
 * - Be vanilla HTML elements or a custom component which passes through a ref
 * - Be focusable - passing through a `tabIndex` attribute
 * - Be an interactive element (button, input, a, etc.) or have an interactive
 *   role (button, link, etc.). Full list of valid elements and roles can be
 *   found
 *   [here](https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/src/useFocusable.tsx).
 */

const CustomTrigger = Focusable

/**
 * A popup that appears when a user hovers or focuses on an element. Useful for
 * providing additional context or information about an element such as a button
 * or icon in a way that doesn't clutter the UI.
 *
 * ```tsx
 * import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
 *
 * <Tooltip content="Learn more">
 *   <IconButton icon={Info} label="Learn more" />
 * </Tooltip>
 * ```
 */

export const Tooltip = ({
  children,
  content,
  delay = 400,
  showArrow,
  tooltipProps: { className: tooltipClassName, ...tooltipProps } = {},
  triggerProps,
}: TooltipProps) => {
  return (
    <AriaTooltipTrigger delay={delay} {...triggerProps}>
      {children}
      <AriaTooltip
        className={twMerge("tt-tooltip", tooltipClassName)}
        {...tooltipProps}
      >
        {showArrow && (
          <OverlayArrow>
            <svg
              className="tt-overlay-arrow"
              width={8}
              height={8}
              viewBox="0 0 8 8"
            >
              <path d="M0 0 L4 4 L8 0" />
            </svg>
          </OverlayArrow>
        )}
        {content}
      </AriaTooltip>
    </AriaTooltipTrigger>
  )
}

Tooltip.Trigger = CustomTrigger
